import React from 'react';
import * as R from 'ramda';
import { withFormik } from 'formik';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { pure, branch, compose, withProps, withState, lifecycle, withHandlers, renderNothing } from 'react-recompose';
// components
import { FormSectionHeader } from '../../../components/form-section-header';
import { FormFooter, TextComponent, ConfirmComponent } from '../../../components';
// features
import { withInitialValuesFromSearchTemplate } from '..//hocs';
import { renderConfirmationModal } from '../../template/helpers';
import { makeSelectInitialDataLoadedStatus } from '../../permission/selectors';
import { changeTemplateItemStatusRequest } from '../../template/report/actions';
import { ReferenceSection } from '../../reference/components/reference-section';
import { makeSelectCurrentBranch, makeSelectCurrentBranchGuid } from '../../branch/selectors';
// forms
import { Form, Fieldset2 } from '../../../forms';
import { getInfo as getItemInfo } from '../../../forms/forms/template-search/components/item-template-search';
// helpers/constants
import * as G from '../../../helpers';
import * as GC from '../../../constants';
// hocs
import { withAsyncSequence } from '../../../hocs';
// ui
import { Box, Flex } from '../../../ui';
// utilities
import { sendRequest } from '../../../utilities/http';
import endpointsMap from '../../../utilities/endpoints';
// feature location
import { ItemsSection } from './items-section';
import { ContactsArray } from './contacts-array';
import { ServicesSection } from './services-section';
import { LocationSectionTitle } from './location-section-title';
import { BusinessHoursSection } from './business-hours-section';
import {
  appointmentFields,
  defaultLocationFields,
  getLocationHeaderFields,
  getLocationFieldSettings,
  locationFieldsValidationSchema,
} from '../settings/fields';
import {
  getCurrentLocationRequest,
  getLocationReferenceRequest,
  deleteLocationReferenceRequest,
  updateLocationReferenceRequest,
  createLocationReferenceRequest,
} from '../actions';
//////////////////////////////////////////////////

const formEnhance = withFormik({
  enableReinitialize: true,
  validationSchema: locationFieldsValidationSchema,
  mapPropsToValues: ({ fromTemplate, asyncSequence, initialValues }) => {
    const initialValuesWithLocationTypeGuid = G.ifElse(
      G.isNotNilAndNotEmpty(initialValues),
      R.assoc(
        GC.FIELD_LOCATION_TYPE,
        R.pathOr('', [GC.FIELD_LOCATION_TYPE, GC.FIELD_DROPDOWN_OPTION_GUID], initialValues),
        initialValues,
      ),
      initialValues,
    );

    const defaultFields = G.ifElse(
      R.and(G.isTrue(fromTemplate), G.isNotNilAndNotEmpty(asyncSequence)),
      R.assoc(GC.FIELD_TEMPLATE_ID, asyncSequence, defaultLocationFields),
      defaultLocationFields,
    );

    return G.setInitialFormikValues(defaultFields, initialValuesWithLocationTypeGuid);
  },
  handleSubmit: (values, { props }) => {
    const { guid, submitAction, asyncSequence, fromTemplate, saveToTemplate } = props;

    const { fcfs, appointmentContact, appointmentComments } = values;

    const residential = R.pathOr(false, ['residential'], values);
    const operationHour = G.convertOperationHoursToDefaultFormat(R.pathOr([], [GC.FIELD_OPERATION_HOUR], values));
    const shippingHour = fcfs ?
      G.convertOperationHoursToDefaultFormat(R.pathOr([], [GC.FIELD_SHIPPING_HOUR], values)) : [];
    const receivingHour = fcfs ?
      G.convertOperationHoursToDefaultFormat(R.pathOr([], [GC.FIELD_RECEIVING_HOUR], values)) : [];
    const appointmentHour = fcfs ?
      [] : G.convertOperationHoursToDefaultFormat(R.pathOr([], [GC.FIELD_APPOINTMENT_HOUR], values));

    const createData = {
      ...values,
      residential,
      [GC.FIELD_APPOINTMENT_COMMENTS]: fcfs ? null : appointmentComments,
      [GC.FIELD_SHIPPING_HOUR]: R.map(R.dissoc(GC.FIELD_GUID), shippingHour),
      [GC.FIELD_RECEIVING_HOUR]: R.map(R.dissoc(GC.FIELD_GUID), receivingHour),
      [GC.FIELD_OPERATION_HOUR]: R.map(R.dissoc(GC.FIELD_GUID), operationHour),
      [GC.FIELD_APPOINTMENT_HOUR]: R.map(R.dissoc(GC.FIELD_GUID), appointmentHour),
      [GC.FIELD_APPOINTMENT_CONTACT]: R.or(fcfs, G.isNilOrEmpty(appointmentContact)) ?
        null :
        R.head(appointmentContact),
    };

    if (G.isTrue(fromTemplate)) {
      const templateData = R.assoc('shouldGetItemList', true, createData);

      if (G.isNotNilAndNotEmpty(guid)) {
        return submitAction({
          ...createData,
          [GC.FIELD_SHIPPING_HOUR]: shippingHour,
          [GC.FIELD_RECEIVING_HOUR]: receivingHour,
          [GC.FIELD_OPERATION_HOUR]: operationHour,
          [GC.FIELD_APPOINTMENT_HOUR]: appointmentHour,
        });
      }

      if (R.equals(G.getPropFromObject(GC.FIELD_TEMPLATE_ID, templateData), asyncSequence)) {
        return submitAction(R.assoc(GC.FIELD_TEMPLATE_ID, null, templateData));
      }

      return submitAction(templateData);
    }

    submitAction(R.assoc('saveToTemplate', saveToTemplate, createData));
  },
});

const enhance = compose(
  withState('references', 'setReferences', []),
  withState('itemOptions', 'setItemOptions', []),
  withState('saveToTemplate', 'setSaveToTemplate', false),
  withHandlers({
    getItemTemplates: (props) => async () => {
      const { setItemOptions } = props;

      const options = {
        data: {
          [GC.BRANCH_GUID]: G.getAmousCurrentBranchGuidFromWindow(),
        },
      };

      const res = await sendRequest('post', endpointsMap.listItems, options);

      const { data, status } = res;

      if (G.isResponseSuccess(status)) {
        const options = data.map((itemTemplate) => ({
          value: G.getGuidFromObject(itemTemplate),
          label: R.trim(`${itemTemplate.itemId}
            ${getItemInfo(itemTemplate)}
            ${R.or(itemTemplate.description, '')}
          `),
        }));

        setItemOptions(options);
      } else {
        G.handleFailResponseSimple(res);
      }
    },
  }),
  withProps(() => ({
    sequenceConfigName: GC.TEMPLATES_LOCATION_TEMPLATE_ID_SEQUENCE,
    autogenerateConfigName: GC.TEMPLATES_LOCATION_TEMPLATE_ID_AUTOGENERATED,
    configsNamesArray: [
      GC.GENERAL_SERVICES,
      GC.TEMPLATES_LOCATION_TYPE,
      GC.TEMPLATES_LOCATION_TEMPLATE_ID_SEQUENCE,
      GC.TEMPLATES_LOCATION_TEMPLATE_ID_AUTOGENERATED,
    ],
  })),
  withAsyncSequence,
  withInitialValuesFromSearchTemplate,
  withProps(({ asyncConfigs }) => ({
    locationTypes: G.mapDropdownConfigWithParentGuid(asyncConfigs, GC.TEMPLATES_LOCATION_TYPE),
    servicesOptions: R.tail(G.mapDropdownConfigWithParentGuid(asyncConfigs, GC.GENERAL_SERVICES)),
  })),
  formEnhance,
  withHandlers({
    handleChangeStatus: (props) => (entity) => {
      const { openModal } = props;

      const { active } = entity;

      const action = G.ifElse(G.isTrue(active), 'deactivate', 'activate');

      const modal = renderConfirmationModal({ ...props, entity, action });

      openModal(modal);
    },
    handleCreateReference: (props) => (values) => {
      const { guid, references, setReferences, createLocationReferenceRequest } = props;

      createLocationReferenceRequest({
        args: R.assoc('primaryObjectGuids', R.of(Array, guid), values),
        callback: (data) => setReferences(R.assoc(G.getGuidFromObject(data), data, references)),
      });
    },
    handleUpdateReference: (props) => (reference) => {
      const { references, setReferences, updateLocationReferenceRequest } = props;

      updateLocationReferenceRequest({
        reference,
        callback: (data) => setReferences(R.assoc(G.getGuidFromObject(data), data, references)),
      });
    },
    handleRemoveReference: (props) => (guid) => {
      const { openModal, closeModal, references, setReferences, deleteLocationReferenceRequest } = props;

      const component = (
        <ConfirmComponent
          name={R.path([guid, GC.FIELD_NAME], references)}
          textLocale={G.getWindowLocale('messages:before:remove', 'Are you sure you want to remove')}
        />
      );

      const modal = {
        component,
        options: {
          width: 600,
          controlButtons: [
            {
              type: 'button',
              name: G.getWindowLocale('actions:remove', 'Remove'),
              action: () => {
                closeModal();

                deleteLocationReferenceRequest({ guid, callback: () => setReferences(R.dissoc(guid, references)) });
              },
            },
          ],
        },
      };

      openModal(modal);
    },
  }),
  branch(
    ({ initialDataLoaded }) => R.not(initialDataLoaded),
    renderNothing,
  ),
  lifecycle({
    componentDidMount() {
      const {
        guid,
        fromTemplate,
        setReferences,
        setInitialValues,
        getItemTemplates,
        getCurrentLocationRequest,
        getLocationReferenceRequest,
      } = this.props;

      if (fromTemplate) getItemTemplates();

      if (G.isAllTrue(fromTemplate, G.isNotNilAndNotEmpty(guid))) {
        getCurrentLocationRequest({ guid, callback: setInitialValues });
        getLocationReferenceRequest({ guid, callback: setReferences });
      }
    },
  }),
  pure,
);

const OwnerInfo = ({ owner }) => (
  <Flex>
    <Box color={G.getTheme('colors.darkGrey')}>{G.getWindowLocale('titles:owner', 'Owner')}:</Box>
    <TextComponent ml='8px' title={owner} maxWidth={290} fontWeight={700} withEllipsis={true}>{owner}</TextComponent>
  </Flex>
);

const HeaderSection = (props) => {
  const { fromTemplate, initialValues, isReadOnlyMode, handleChangeStatus, isEditTemplateMode } = props;

  const isLocationActive = R.pathOr(false, [GC.FIELD_ACTIVE], initialValues);

  const fieldSettings = R.map(
    R.assoc('disabled', isReadOnlyMode),
    getLocationHeaderFields({ fromTemplate, isEditTemplateMode, active: isLocationActive }),
  );

  return (
    <Fieldset2
      {...G.getFormikProps(props)}
      fields={fieldSettings}
      handleCustomChange={() => handleChangeStatus(initialValues)}
      fieldsWrapperStyles={{ width: 520, gap: 50, justifyContent: 'flex-end' }}
    />
  );
};

const getActionButtons = (setSaveToTemplate) => [{
  type: 'submit',
  buttonStyles: { width: 180 },
  action: () => setSaveToTemplate(true),
  displayText: G.getWindowLocale('titles:save-and-add-to-template', 'Save & Add to Template'),
}];

const LocationForm = (props) => {
  const {
    guid,
    branch,
    values,
    fromTemplate,
    handleSubmit,
    locationTypes,
    isReadOnlyMode,
    setSaveToTemplate,
  } = props;

  const { fcfs, integrationType, appointmentHour } = values;

  const submitBtnStyles = G.ifElse(isReadOnlyMode, { display: 'none' }, {});
  const isEditTemplateMode = G.isAllTrue(fromTemplate, G.isNotNilAndNotEmpty(guid));
  const fieldSettings = R.map(R.assoc('disabled', isReadOnlyMode), getLocationFieldSettings(integrationType));


  return (
    <Box
      width={910}
      overflow='auto'
      maxHeight='87vh'
    >
      <Form onSubmit={handleSubmit}>
        <FormSectionHeader expanded={true} title={<LocationSectionTitle {...props} />}>
          <Flex p='10px 15px 0 20px' justifyContent='space-between'>
            <OwnerInfo owner={R.pathOr('', [GC.FIELD_BRANCH_NAME], branch)} />
            <HeaderSection {...props} isEditTemplateMode={isEditTemplateMode} />
          </Flex>
          <Fieldset2
            {...G.getFormikProps(props)}
            fields={fieldSettings}
            locationTypes={locationTypes}
            fieldsWrapperStyles={{ mt: 25, mb: 20, px: 15, justifyContent: 'space-between' }}
          />
        </FormSectionHeader>
        {
          G.isTrue(fromTemplate) &&
          <BusinessHoursSection
            {...props}
            useFormikSetFieldValue={true}
            operationHour={R.pathOr([], [GC.FIELD_OPERATION_HOUR], values)}
          />
        }
        {
          G.isTrue(fromTemplate) && fcfs &&
          <BusinessHoursSection
            {...props}
            useFormikSetFieldValue={true}
            fieldName={GC.FIELD_SHIPPING_HOUR}
            operationHour={R.pathOr([], [GC.FIELD_SHIPPING_HOUR], values)}
            title={G.getWindowLocale('titles:shipping-hours', 'Shipping Hours')}
          />
        }
        {
          G.isTrue(fromTemplate) && fcfs &&
          <BusinessHoursSection
            {...props}
            useFormikSetFieldValue={true}
            fieldName={GC.FIELD_RECEIVING_HOUR}
            operationHour={R.pathOr([], [GC.FIELD_RECEIVING_HOUR], values)}
            title={G.getWindowLocale('titles:receiving-hours', 'Receiving Hours')}
          />
        }
        {
          G.isTrue(fromTemplate) && R.not(fcfs) &&
          <Box>
            <BusinessHoursSection
              {...props}
              showAppointmentFields={true}
              useFormikSetFieldValue={true}
              fieldName={GC.FIELD_APPOINTMENT_HOUR}
              operationHour={R.pathOr([], [GC.FIELD_APPOINTMENT_HOUR], values)}
              title={G.getWindowLocale('titles:appointment-hours', 'Appointment Hours')}
            />
            {
              G.isNotNilAndNotEmpty(appointmentHour) &&
              <Box>
                <Fieldset2
                  fields={appointmentFields}
                  {...G.getFormikProps(props)}
                  locationTypes={locationTypes}
                  fieldsWrapperStyles={{ mt: 20, mb: 20, px: 15 }}
                />
                <ContactsArray
                  {...props}
                  maxItems={1}
                  fieldSettings={fieldSettings}
                  fieldName={GC.FIELD_APPOINTMENT_CONTACT}
                  contacts={R.pathOr([], [GC.FIELD_APPOINTMENT_CONTACT], values)}
                  title={G.getWindowLocale('titles:appointment-contact', 'Appointment Contact')}
                />
              </Box>
            }
          </Box>
        }
        <ContactsArray {...props} contacts={R.pathOr([], [GC.FIELD_CONTACTS], values)} />
        {
          G.isTrue(fromTemplate) &&
          <ServicesSection {...props} serviceGuids={R.pathOr([], [GC.FIELD_SERVICE_GUIDS], values)} />
        }
        {isEditTemplateMode && <ReferenceSection {...props} scope={GC.REF_SCOPE_NAME_LOCATION} />}
        {
          G.isTrue(fromTemplate) &&
          <ItemsSection {...props} itemTemplateGuids={R.pathOr([], [GC.FIELD_ITEM_TEMPLATE_GUIDS], values)} />
        }
        <FormFooter
          submitBtnStyles={submitBtnStyles}
          submitAction={() => setSaveToTemplate(false)}
          boxStyles={{ p: 15, bg: G.getTheme('colors.whiteGrey') }}
          actionButtons={G.ifElse(
            R.and(G.isNilOrFalse(fromTemplate), G.isNilOrEmpty(guid)),
            getActionButtons(setSaveToTemplate),
            [],
          )}
        />
      </Form>
    </Box>
  );
};

const mapStateToProps = (state) => createStructuredSelector({
  branch: makeSelectCurrentBranch(state),
  branchGuid: makeSelectCurrentBranchGuid(state),
  initialDataLoaded: makeSelectInitialDataLoadedStatus(state),
});

export default connect(mapStateToProps, {
  getCurrentLocationRequest,
  getLocationReferenceRequest,
  deleteLocationReferenceRequest,
  updateLocationReferenceRequest,
  createLocationReferenceRequest,
  changeTemplateItemStatusRequest,
})(enhance(LocationForm));
